import { useState, useEffect } from "react";
import {
  Settings,
  Image as ImageIcon,
  Video,
  Globe,
  Save,
  Upload,
  X,
  Layout,
  Plus,
  Trash2,
  Edit2,
  Eye,
  RefreshCw,
  ToggleLeft,
  ToggleRight,
} from "lucide-react";
import BaseApi from "../../services/base-api";
import { useToast } from "../../context/ToastContext";
import { useTranslation } from "react-i18next";
import ConfirmModal from "../../components/Modals/Confirm";
import LoadingComponent from "../../components/LoadingComponent";
import { BannerApi, BannerPayload } from "../../services/banner-apis";
// ... Keep all existing interfaces (CompanySettings, TeamMember, BusinessHours, SocialLinkData)
// I'll include them here for completeness

type RecipientField = "emails" | "numbers";
type BannerMediaTab = "image" | "video";
type BannerDisplayMode = "image" | "video";

const PAYMENT_PROVIDER_OPTIONS = [
  { value: "myfatoorah", label: "MyFatoorah" },
  { value: "skipcash", label: "SkipCash" },
];

const OTP_CHANNEL_OPTIONS = [
  { value: "sms", label: "SMS" },
  { value: "whatsapp", label: "WhatsApp" },
];

const extractUrlFromResponse = (obj: any): string | null => {
  if (
    typeof obj === "string" &&
    (obj.startsWith("http") || obj.startsWith("/"))
  ) {
    return obj;
  }

  if (obj && typeof obj === "object") {
    const urlFields = [
      "url",
      "imageUrl",
      "path",
      "filePath",
      "location",
      "link",
      "data",
    ];
    for (const field of urlFields) {
      if (obj[field] && typeof obj[field] === "string") {
        return obj[field];
      }
    }

    for (const key in obj) {
      const result = extractUrlFromResponse(obj[key]);
      if (result) return result;
    }
  }

  return null;
};

const validateImageDimensions = (
  file: File,
  requiredWidth = 1600,
  requiredHeight = 400,
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);

      if (img.width === requiredWidth && img.height === requiredHeight) {
        resolve();
      } else {
        reject(
          new Error(
            `Image must be exactly ${requiredWidth}×${requiredHeight}px`,
          ),
        );
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Invalid image file"));
    };

    img.src = url;
  });
};

interface CompanySettings {
  // Company Information
  companyName: string;
  companyEmail: string;
  companyPhone: string;
  companyAddress: string;
  companyWebsite: string;
  companyDescription: string;

  // Branding
  logo: string;
  favicon: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;

  // Banners & Images
  homeBanner: string;
  aboutBanner: string;
  contactBanner: string;
  defaultProductImage: string;

  // Content
  aboutUs: string;
  termsAndConditions: string;
  privacyPolicy: string;
  returnPolicy: string;
  shippingPolicy: string;

  // Features & Permissions
  enableRatings: boolean;
  enableReviews: boolean;
  enableWishlist: boolean;
  enableCompare: boolean;
  enableGuestCheckout: boolean;
  enableNewsletter: boolean;
  enableLiveChat: boolean;

  // Custom Messages
  welcomeMessage: string;
  checkoutMessage: string;
  orderConfirmationMessage: string;
  outOfStockMessage: string;
  maintenanceMessage: string;

  // Social Media
  facebook: string;
  instagram: string;
  twitter: string;
  linkedin: string;
  youtube: string;

  // Team Members
  teamMembers: TeamMember[];

  // SEO
  metaTitle: string;
  metaDescription: string;
  metaKeywords: string;

  // Notifications
  emailNotifications: boolean;
  smsNotifications: boolean;
  pushNotifications: boolean;

  // Business Hours
  businessHours: BusinessHours[];
}

interface TeamMember {
  id: string;
  name: string;
  position: string;
  bio: string;
  image: string;
  email: string;
  phone: string;
  socialLinks: {
    linkedin?: string;
    twitter?: string;
  };
}

interface BusinessHours {
  day: string;
  isOpen: boolean;
  openTime: string;
  closeTime: string;
}

export interface SocialLinkData {
  id?: string;
  platform: string;
  type: string;
  url: string;
  displayName: string;
  username: string;
  // icon: any;
  description: string;
  sortOrder: number;
}

interface AppSetting {
  id: string;
  key: string;
  value: boolean | string | number | string[] | number[] | null;
  type: string;
  group: string;
  description?: string;
  isPublic?: boolean;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string | null;
}

const CompanySettingsPage = () => {
  const { success, err } = useToast();
  const { t, i18n } = useTranslation();

  // ... Keep existing defaultSettings
  const defaultSettings: CompanySettings = {
    companyName: "{APP_NAME}",
    companyEmail: "support@maagoud.com",
    companyPhone: "",
    companyAddress: "",
    companyWebsite: "",
    companyDescription: "",
    logo: "",
    favicon: "",
    primaryColor: "#4F46E5",
    secondaryColor: "#7C3AED",
    accentColor: "#EC4899",
    homeBanner: "",
    aboutBanner: "",
    contactBanner: "",
    defaultProductImage: "",
    aboutUs: "",
    termsAndConditions: "",
    privacyPolicy: "",
    returnPolicy: "",
    shippingPolicy: "",
    enableRatings: true,
    enableReviews: true,
    enableWishlist: true,
    enableCompare: false,
    enableGuestCheckout: true,
    enableNewsletter: true,
    enableLiveChat: false,
    welcomeMessage: "",
    checkoutMessage: "",
    orderConfirmationMessage: "",
    outOfStockMessage: "",
    maintenanceMessage: "",
    facebook: "",
    instagram: "",
    twitter: "",
    linkedin: "",
    youtube: "",
    teamMembers: [],
    metaTitle: "",
    metaDescription: "",
    metaKeywords: "",
    emailNotifications: true,
    smsNotifications: false,
    pushNotifications: false,
    businessHours: [
      { day: "Monday", isOpen: true, openTime: "09:00", closeTime: "18:00" },
      { day: "Tuesday", isOpen: true, openTime: "09:00", closeTime: "18:00" },
      { day: "Wednesday", isOpen: true, openTime: "09:00", closeTime: "18:00" },
      { day: "Thursday", isOpen: true, openTime: "09:00", closeTime: "18:00" },
      { day: "Friday", isOpen: false, openTime: "", closeTime: "" },
      { day: "Saturday", isOpen: true, openTime: "10:00", closeTime: "16:00" },
      { day: "Sunday", isOpen: true, openTime: "09:00", closeTime: "18:00" },
    ],
  };

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("social");

  // Banner management state
  const [banners, setBanners] = useState<BannerPayload[]>([]);
  const [selectedBanner, setSelectedBanner] = useState<BannerPayload | null>(
    null,
  );
  const [showBannerForm, setShowBannerForm] = useState(false);
  const [bannerMediaTab, setBannerMediaTab] = useState<BannerMediaTab>("image");
  const [uploadingBannerImage, setUploadingBannerImage] = useState(false);
  const [localImagePreview, setLocalImagePreview] = useState<string>("");
  const [localVideoPreview, setLocalVideoPreview] = useState<string>("");
  const [imageFile, setImageFile] = useState<string | null>(null);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [previewVideo, setPreviewVideo] = useState<{
    src: string;
    title: string;
  } | null>(null);

  // Social links saving state
  const [savingSocialLink, setSavingSocialLink] = useState<string | null>(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [payAndDeliver, setPayAndDeliver] = useState(false);
  const [updatingPayAndDeliver, setUpdatingPayAndDeliver] = useState(false);
  const [paymentProvider, setPaymentProvider] = useState("myfatoorah");
  const [savedPaymentProvider, setSavedPaymentProvider] =
    useState("myfatoorah");
  const [updatingPaymentProvider, setUpdatingPaymentProvider] = useState(false);
  const [otpChannel, setOtpChannel] = useState("sms");
  const [savedOtpChannel, setSavedOtpChannel] = useState("sms");
  const [updatingOtpChannel, setUpdatingOtpChannel] = useState(false);
  const [bannerDisplayMode, setBannerDisplayMode] =
    useState<BannerDisplayMode>("image");
  const [savedBannerDisplayMode, setSavedBannerDisplayMode] =
    useState<BannerDisplayMode>("image");
  const [updatingBannerDisplayMode, setUpdatingBannerDisplayMode] =
    useState(false);
  const [savingReportRecipients, setSavingReportRecipients] = useState(false);
  const [editingReportRecipients, setEditingReportRecipients] = useState(false);
  const [reportRecipientInputs, setReportRecipientInputs] = useState<{
    emails: string;
    numbers: string;
  }>({
    emails: "",
    numbers: "",
  });
  const [savedReportRecipientInputs, setSavedReportRecipientInputs] = useState<{
    emails: string;
    numbers: string;
  }>({
    emails: "",
    numbers: "",
  });

  // Social links data with additional fields
  const [socialLinksData, setSocialLinksData] = useState<
    Record<string, SocialLinkData>
  >({
    tiktok: {
      platform: "tiktok",
      type: "profile",
      url: "",
      displayName: "TikTok",
      username: "",
      description: "",
      sortOrder: 0,
    },
    instagram: {
      platform: "instagram",
      type: "profile",
      url: "",
      displayName: "Instagram",
      username: "",
      description: "",
      sortOrder: 1,
    },
    twitter: {
      platform: "twitter",
      type: "profile",
      url: "",
      displayName: "Twitter",
      username: "",
      description: "",
      sortOrder: 2,
    },
    linkedin: {
      platform: "linkedin",
      type: "profile",
      url: "",
      displayName: "LinkedIn",
      username: "",
      description: "",
      sortOrder: 3,
    },
  });

  const [confirmConfig, setConfirmConfig] = useState({
    isOpen: false,
    title: "",
    message: "",
    confirmText: t("Confirm"),
    cancelText: t("Cancel"),
    onConfirm: () => {},
  });

  useEffect(() => {
    fetchSocialLinks();
    fetchBanners();
    fetchAdvancedSettings();
  }, []);

  const fetchSocialLinks = async () => {
    try {
      setLoading(true);
      const res = await BaseApi.get("/social-links");
      if (res.success && res.data) {
        const updatedLinks = { ...socialLinksData };
        res.data.forEach((link: SocialLinkData) => {
          if (updatedLinks[link.platform]) {
            updatedLinks[link.platform] = link;
          }
        });
        setSocialLinksData(updatedLinks);
      }
    } catch (error) {
      console.error("Error fetching social links:", error);
    } finally {
      setLoading(false);
    }
  };

  // Banner Management Functions
  const fetchBanners = async () => {
    try {
      const res = await BannerApi.getAll();
      if (res.success && res.data) {
        setBanners(res.data);
      }
    } catch (error) {
      console.error("Error fetching banners:", error);
    }
  };

  const getMediaSrc = (media?: string | string[]) =>
    Array.isArray(media) ? media[0] : media;

  const isVideoFile = (value?: string) =>
    !!value &&
    /\.(mp4|webm|ogg|mov|m4v)(\?.*)?$/i.test(value.split("#")[0] || value);

  const getBannerVideoSrc = (banner?: BannerPayload | null) => {
    if (!banner) return "";

    const directVideo = getMediaSrc(banner.video);
    if (directVideo) return directVideo;

    if (banner.videoUrl) return banner.videoUrl;

    const imageSrc = getMediaSrc(banner.image);
    return isVideoFile(imageSrc) ? imageSrc || "" : "";
  };

  const isVideoBanner = (banner?: BannerPayload | null) => {
    if (!banner) return false;
    return banner.mediaType === "video" || !!getBannerVideoSrc(banner);
  };

  const resetBannerForm = () => {
    setShowBannerForm(false);
    setSelectedBanner(null);
    setLocalImagePreview("");
    setLocalVideoPreview("");
    setImageFile(null);
    setVideoFile(null);
    setPreviewVideo(null);
  };

  const imageBanners = banners.filter((banner) => !isVideoBanner(banner));
  const videoBanners = banners.filter((banner) => isVideoBanner(banner));
  const visibleBanners =
    bannerMediaTab === "image" ? imageBanners : videoBanners;

  const normalizeBooleanSetting = (value: unknown) => {
    if (typeof value === "boolean") return value;
    if (typeof value === "string") return value.toLowerCase() === "true";
    if (typeof value === "number") return value === 1;
    return false;
  };

  const normalizeStringArraySetting = (value: unknown) => {
    if (Array.isArray(value)) {
      return value.map((item) => String(item || "").trim()).filter(Boolean);
    }

    return [];
  };

  const normalizeStringSetting = (value: unknown) => {
    if (typeof value === "string") return value.trim();
    if (typeof value === "number" || typeof value === "boolean") {
      return String(value);
    }

    return "";
  };

  const parseRecipientInput = (value: string) =>
    value
      .split(/\r?\n|,/)
      .map((item) => item.trim())
      .filter(Boolean);

  const fetchAdvancedSettings = async () => {
    try {
      setSettingsLoading(true);
      const res = await BaseApi.get("/settings");

      if (res.success && Array.isArray(res.data)) {
        const payAndDeliverSetting = (res.data as AppSetting[]).find(
          (setting) => setting.key === "pay_and_deliver",
        );

        if (payAndDeliverSetting) {
          setPayAndDeliver(normalizeBooleanSetting(payAndDeliverSetting.value));
        }

        const paymentProviderSetting = (res.data as AppSetting[]).find(
          (setting) => setting.key === "payment_provider",
        );

        if (paymentProviderSetting) {
          const providerValue = normalizeStringSetting(
            paymentProviderSetting.value,
          );

          if (providerValue) {
            setPaymentProvider(providerValue);
            setSavedPaymentProvider(providerValue);
          }
        }

        const otpChannelSetting = (res.data as AppSetting[]).find(
          (setting) => setting.key === "otp_channel",
        );

        if (otpChannelSetting) {
          const channelValue = normalizeStringSetting(otpChannelSetting.value);

          if (
            OTP_CHANNEL_OPTIONS.some((option) => option.value === channelValue)
          ) {
            setOtpChannel(channelValue);
            setSavedOtpChannel(channelValue);
          }
        }

        const bannerDisplayModeSetting = (res.data as AppSetting[]).find(
          (setting) => setting.key === "banner_display_mode",
        );

        if (bannerDisplayModeSetting) {
          const bannerDisplayModeValue = normalizeStringSetting(
            bannerDisplayModeSetting.value,
          );

          if (
            bannerDisplayModeValue === "image" ||
            bannerDisplayModeValue === "video"
          ) {
            setBannerDisplayMode(bannerDisplayModeValue);
            setSavedBannerDisplayMode(bannerDisplayModeValue);
          }
        }

        const emailSetting = (res.data as AppSetting[]).find(
          (setting) => setting.key === "emails",
        );
        const numberSetting = (res.data as AppSetting[]).find(
          (setting) => setting.key === "numbers",
        );

        const nextReportRecipientInputs = {
          emails: normalizeStringArraySetting(emailSetting?.value).join("\n"),
          numbers: normalizeStringArraySetting(numberSetting?.value).join("\n"),
        };

        setReportRecipientInputs(nextReportRecipientInputs);
        setSavedReportRecipientInputs(nextReportRecipientInputs);
      }
    } catch (error) {
      console.error("Error fetching advanced settings:", error);
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleTogglePayAndDeliver = async () => {
    const nextValue = !payAndDeliver;

    try {
      setUpdatingPayAndDeliver(true);
      const res = await BaseApi.put("/settings/pay_and_deliver", {
        value: nextValue,
      });

      if (res.success) {
        setPayAndDeliver(nextValue);
        success(t("Pay and deliver setting updated"));
      } else {
        err(t("Failed to update pay and deliver setting"));
      }
    } catch (error) {
      console.error("Error updating pay and deliver setting:", error);
      err(t("Failed to update pay and deliver setting"));
    } finally {
      setUpdatingPayAndDeliver(false);
    }
  };

  const handleSavePaymentProvider = async () => {
    try {
      setUpdatingPaymentProvider(true);
      const res = await BaseApi.put("/settings/payment_provider", {
        value: paymentProvider,
      });

      if (res.success) {
        setSavedPaymentProvider(paymentProvider);
        success(t("Payment provider updated"));
      } else {
        err(t("Failed to update payment provider"));
      }
    } catch (error) {
      console.error("Error updating payment provider:", error);
      err(t("Failed to update payment provider"));
    } finally {
      setUpdatingPaymentProvider(false);
    }
  };

  const handleSaveOtpChannel = async () => {
    try {
      setUpdatingOtpChannel(true);
      const res = await BaseApi.put("/settings/otp_channel", {
        value: otpChannel,
      });

      if (res.success) {
        setSavedOtpChannel(otpChannel);
        success(t("OTP channel updated"));
      } else {
        err(t("Failed to update OTP channel"));
      }
    } catch (error) {
      console.error("Error updating OTP channel:", error);
      err(t("Failed to update OTP channel"));
    } finally {
      setUpdatingOtpChannel(false);
    }
  };

  const handleSaveBannerDisplayMode = async () => {
    try {
      setUpdatingBannerDisplayMode(true);
      const res = await BaseApi.put("/settings/banner_display_mode", {
        value: bannerDisplayMode,
      });

      if (res.success) {
        setSavedBannerDisplayMode(bannerDisplayMode);
        success(t("Banner display mode updated"));
      } else {
        err(t("Failed to update banner display mode"));
      }
    } catch (error) {
      console.error("Error updating banner display mode:", error);
      err(t("Failed to update banner display mode"));
    } finally {
      setUpdatingBannerDisplayMode(false);
    }
  };

  const handleReportRecipientInputChange = (
    field: RecipientField,
    value: string,
  ) => {
    setReportRecipientInputs((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveReportRecipients = async () => {
    try {
      setSavingReportRecipients(true);

      const emails = parseRecipientInput(reportRecipientInputs.emails);
      const numbers = parseRecipientInput(reportRecipientInputs.numbers);

      const [emailsRes, numbersRes] = await Promise.all([
        BaseApi.put("/settings/emails", { value: emails }),
        BaseApi.put("/settings/numbers", { value: numbers }),
      ]);

      if (emailsRes.success && numbersRes.success) {
        setSavedReportRecipientInputs(reportRecipientInputs);
        setEditingReportRecipients(false);
        success(t("Report recipients updated"));
      } else {
        err(t("Failed to update report recipients"));
      }
    } catch (error) {
      console.error("Error updating report recipients:", error);
      err(t("Failed to update report recipients"));
    } finally {
      setSavingReportRecipients(false);
    }
  };

  const handleCancelReportRecipientEdit = () => {
    setReportRecipientInputs(savedReportRecipientInputs);
    setEditingReportRecipients(false);
  };

  const paymentProviderOptions = PAYMENT_PROVIDER_OPTIONS.some(
    (option) => option.value === paymentProvider,
  )
    ? PAYMENT_PROVIDER_OPTIONS
    : [
        ...PAYMENT_PROVIDER_OPTIONS,
        {
          value: paymentProvider,
          label: paymentProvider,
        },
      ];

  const handleCreateBanner = (mediaType: BannerMediaTab = bannerMediaTab) => {
    setBannerMediaTab(mediaType);
    setSelectedBanner({
      title: { en: "", ar: "" },
      image: "",
      video: "",
      videoUrl: "",
      mediaType,
      position: "home_main",
      isActive: true,
      sortOrder: banners.length,
      startDate: new Date().toISOString(),
      endDate: "",
    });
    setLocalImagePreview("");
    setLocalVideoPreview("");
    setImageFile(null);
    setVideoFile(null);
    setShowBannerForm(true);
  };

  const handleEditBanner = (banner: BannerPayload) => {
    const mediaType = isVideoBanner(banner) ? "video" : "image";
    setBannerMediaTab(mediaType);
    setSelectedBanner(banner);
    setLocalImagePreview(mediaType === "image" ? getMediaSrc(banner.image) || "" : "");
    setLocalVideoPreview(mediaType === "video" ? getBannerVideoSrc(banner) : "");
    setImageFile(null);
    setVideoFile(null);
    setShowBannerForm(true);
  };

  const handleBannerImageUpload = async (
    file: File,
    isMobile: boolean = false,
  ) => {
    try {
      setUploadingBannerImage(true);

      const res = await BannerApi.uploadImage(file);
      if (res.success) {
        console.log("Upload response:", res);

        // Extract URL from various possible response structures
        let imageUrl: string | null = null;

        // Helper function to recursively search for URL in response
        const extractUrl = (obj: any): string | null => {
          if (
            typeof obj === "string" &&
            (obj.startsWith("http") || obj.startsWith("/"))
          ) {
            return obj;
          }

          if (obj && typeof obj === "object") {
            // Check common URL field names
            const urlFields = [
              "url",
              "imageUrl",
              "path",
              "filePath",
              "location",
              "link",
            ];
            for (const field of urlFields) {
              if (obj[field] && typeof obj[field] === "string") {
                return obj[field];
              }
            }

            // Recursively check nested objects
            for (const key in obj) {
              const result = extractUrl(obj[key]);
              if (result) return result;
            }
          }

          return null;
        };

        imageUrl = extractUrl(res);

        if (imageUrl) {
          setSelectedBanner((prev) =>
            prev
              ? {
                  ...prev,
                  [isMobile ? "mobileImage" : "image"]: imageUrl,
                }
              : null,
          );
          console.log("imageUrl - ", imageUrl);
          success(
            t(
              isMobile
                ? "Mobile banner image uploaded"
                : "Banner image uploaded",
            ),
          );
        } else {
          console.error("Upload failed - No URL found in response:", res);
          err(t("Failed to upload image - No URL returned"));
          // Revert to empty if upload failed
          setSelectedBanner((prev) =>
            prev
              ? {
                  ...prev,
                  [isMobile ? "mobileImage" : "image"]: "",
                }
              : null,
          );
        }
      }
    } catch (error: any) {
      console.error("Upload error:", error);
      setSelectedBanner((prev) =>
        prev
          ? {
              ...prev,
              image: "",
            }
          : null,
      );
    } finally {
      setUploadingBannerImage(false);
    }
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Then in your save function, upload the file first:
  const handleSaveBanner = async () => {
    if (!selectedBanner) return;

    try {
      setSaving(true);

      const mediaType = bannerMediaTab === "video" ? "video" : "image";
      const savedImage = imageFile || getMediaSrc(selectedBanner.image) || "";
      let savedVideo = getBannerVideoSrc(selectedBanner) || "";

      if (!selectedBanner.endDate) {
        err(t("Please select an end date"));
        return;
      }

      if (mediaType === "image" && !savedImage) {
        err(t("Please upload a banner image"));
        return;
      }

      if (mediaType === "video") {
        if (videoFile) {
          const uploadRes = await BannerApi.uploadMedia(videoFile);
          const uploadedVideoUrl =
            uploadRes.data?.url || extractUrlFromResponse(uploadRes);

          if (!uploadedVideoUrl) {
            err(t("Failed to upload video"));
            return;
          }

          savedVideo = uploadedVideoUrl;
        }

        if (!savedVideo) {
          err(t("Please upload a banner video"));
          return;
        }
      }

      const bannerData = {
        ...selectedBanner,
        mediaType,
        image: mediaType === "image" ? savedImage : "",
        mobileImage: mediaType === "image" ? savedImage : "",
        video: mediaType === "video" ? savedVideo : "",
        mobileVideo: mediaType === "video" ? savedVideo : "",
        videoUrl: mediaType === "video" ? savedVideo : "",
      };

      const res = selectedBanner.id
        ? await BannerApi.update(selectedBanner.id, bannerData)
        : await BannerApi.create(bannerData);

      if (res.success) {
        success(
          t(mediaType === "video" ? "Video saved successfully" : "Banner saved successfully"),
        );
        resetBannerForm();
        fetchBanners();
      }
    } catch (error) {
      console.error("Failed to save banner", error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteBanner = async (bannerId: string) => {
    try {
      const res = await BannerApi.delete(bannerId);
      if (res.success) {
        success(t("Banner deleted successfully"));
        fetchBanners();
      } else {
        err(t("Failed to delete banner"));
      }
    } catch (error) {
      console.error(t("Failed to delete banner"), error);
    }
  };

  const handleToggleBanner = async (bannerId: string) => {
    try {
      const res = await BannerApi.toggle(bannerId);
      if (res.success) {
        success(t("Banner status updated"));
        fetchBanners();
      } else {
        err(t("Failed to update banner status"));
      }
    } catch (error) {
      console.error(t("Failed to update banner"), error);
    }
  };

  const openDeleteConfirm = (bannerId: string) => {
    setConfirmConfig({
      isOpen: true,
      title: t("Delete Banner"),
      message: t(
        "Are you sure you want to delete this banner? This action cannot be undone.",
      ),
      confirmText: t("Delete"),
      cancelText: t("Cancel"),
      onConfirm: () => {
        handleDeleteBanner(bannerId);
        closeConfirmModal();
      },
    });
  };

  const closeConfirmModal = () => {
    setConfirmConfig({
      ...confirmConfig,
      isOpen: false,
    });
  };

  const handleUpdateSocialLink = async (platform: string) => {
    try {
      const socialLink = socialLinksData[platform];

      if (!socialLink.url) {
        err(t("Please enter a URL"));
        return;
      }

      setSavingSocialLink(platform);

      try {
        const payload = {
          platform: socialLink.platform,
          type: socialLink.type,
          url: socialLink.url,
          displayName: socialLink.displayName,
          username: socialLink.username,
          description: socialLink.description,
          sortOrder: socialLink.sortOrder,
        };

        const res = socialLink.id
          ? await BaseApi.put(`/social-links/${socialLink.id}`, payload)
          : await BaseApi.post("/social-links", payload);

        if (res.success) {
          success(t(`${socialLink.displayName} saved successfully`));
          if (res.data) {
            setSocialLinksData((prev) => ({
              ...prev,
              [platform]: { ...socialLink, ...res.data },
            }));
          }
        } else {
          err(t("Failed to save social link"));
        }
      } catch (apiError) {
        console.error("Error saving social link:", apiError);
      }
    } catch (error) {
      console.error("Error saving social link:", error);
    } finally {
      setSavingSocialLink(null);
    }
  };

  // Tabs configuration
  const tabs = [
    // { id: "company", label: t("Company Info"), icon: Building2 },
    { id: "social", label: t("Social Links"), icon: Globe },
    { id: "banner", label: t("Banners"), icon: Layout },
    { id: "advanced", label: t("Advanced Settings"), icon: Settings },
  ];

  if (loading) {
    return <LoadingComponent title={t("Settings")} />;
  }

  const handleBannerMediaTabChange = (tab: BannerMediaTab) => {
    setBannerMediaTab(tab);
    resetBannerForm();
  };

  const getImageSrc = (image?: string | string[]) => getMediaSrc(image);

  return (
    <div className="min-h-screen bg-[#f2e6db] p-4 sm:p-6 lg:p-8">
      <div className="mx-auto">
        {/* Header */}
        <div className="animate-slide-up mb-4">
          <div className="bg-white rounded-3xl shadow-2xl p-6 sm:p-8 border border-gray-100">
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
              <div className="flex items-center gap-5">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-gray-700 to-black rounded-2xl blur-lg opacity-50"></div>
                  <div className="relative bg-gradient-to-br from-gray-700 to-black rounded-2xl p-4">
                    <Settings className="w-8 h-8 sm:w-6 sm:h-6 text-white" />
                  </div>
                </div>
                <div>
                  <h1 className="text-4xl font-bold gradient-text mb-1">
                    {t("Company Settings")}
                  </h1>
                  <p className="text-gray-600 text-sm">
                    {t("Manage your company information and preferences")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 mb-4 p-2">
          <div className="flex flex-wrap gap-2">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
                    activeTab === tab.id
                      ? "bg-gradient-to-r from-gray-600 to-black text-white shadow-lg"
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  <Icon size={20} />
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 sm:p-8">
          {/* Social Links Tab */}
          {activeTab === "social" && (
            <div className="space-y-6">
              <div className="pb-6 border-b border-gray-100">
                <div className="flex items-center gap-3 mb-2">
                  <div className="bg-gradient-to-br from-gray-500 to-gray-600 rounded-lg p-2">
                    <Globe size={22} className="text-white" />
                  </div>
                  <h2 className="text-3xl font-bold text-gray-800">
                    {t("Social Media Links")}
                  </h2>
                </div>
                <p className="text-gray-500 mt-2 ml-12">
                  {t("Connect your social media profiles")}
                </p>
              </div>

              <div className="space-y-4">
                {Object.entries(socialLinksData).map(([key, social], index) => {
                  return (
                    <div
                      key={key}
                      style={{ animationDelay: `${index * 0.1}s` }}
                      className="bg-white rounded-2xl border-2 border-gray-100 p-6"
                    >
                      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-end">
                        {/* URL Input */}
                        <div className="lg:col-span-8">
                          <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-2">
                            <span>{social.displayName}</span>
                            <span className="text-gray-400 font-normal">
                              - {t("Profile URL")}
                            </span>
                            <span className="text-red-500 ml-1">*</span>
                          </label>
                          <input
                            type="url"
                            value={social.url}
                            onChange={(e) =>
                              setSocialLinksData((prev) => ({
                                ...prev,
                                [key]: { ...social, url: e.target.value },
                              }))
                            }
                            className="w-full px-4 py-3.5 rounded-xl border-2 border-gray-200 focus:border-[#0d223f] focus:outline-none text-gray-700 placeholder-gray-400 bg-gray-50/50"
                            placeholder={`https://${social.platform}.com/yourcompany`}
                          />
                        </div>

                        {/* Username Input */}
                        <div className="lg:col-span-4">
                          <label className="block text-sm font-semibold text-gray-600 mb-2">
                            {t("Username")}
                          </label>
                          <input
                            type="text"
                            value={social.username}
                            onChange={(e) =>
                              setSocialLinksData((prev) => ({
                                ...prev,
                                [key]: { ...social, username: e.target.value },
                              }))
                            }
                            className="w-full px-4 py-3.5 rounded-xl border-2 border-gray-200 focus:border-[#0d223f] focus:outline-none text-gray-700 placeholder-gray-400 bg-gray-50/50"
                            placeholder="@yourcompany"
                          />
                        </div>

                        {/* Description */}
                        <div className="lg:col-span-8">
                          <label className="block text-sm font-semibold text-gray-600 mb-2">
                            {t("Description")}
                          </label>
                          <textarea
                            value={social.description}
                            onChange={(e) =>
                              setSocialLinksData((prev) => ({
                                ...prev,
                                [key]: {
                                  ...social,
                                  description: e.target.value,
                                },
                              }))
                            }
                            className="w-full px-4 py-3.5 rounded-xl border-2 border-gray-200 focus:border-[#0d223f] focus:outline-none resize-none text-gray-700 placeholder-gray-400 bg-gray-50/50"
                            rows={2}
                            placeholder={t("Optional description")}
                          />
                        </div>

                        {/* Save Button */}
                        <div className="lg:col-span-4 flex justify-end items-end">
                          <button
                            onClick={() => handleUpdateSocialLink(key)}
                            disabled={savingSocialLink === key || !social.url}
                            className="h-14 w-full lg:w-14 bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-600 hover:to-black text-white rounded-xl transition-all duration-300 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed shadow-lg hover:shadow-xl disabled:shadow-none"
                            title={t("Save")}
                          >
                            {savingSocialLink === key ? (
                              <RefreshCw size={20} className="animate-spin" />
                            ) : (
                              <Save size={22} />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Banner Tab */}
          {activeTab === "banner" && (
            <div className="space-y-6">
              <div className="pb-6 border-b border-gray-100">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="bg-gradient-to-br from-gray-500 to-gray-600 rounded-lg p-2">
                        <Layout size={22} className="text-white" />
                      </div>
                      <h2 className="text-3xl font-bold text-gray-800">
                        {t("Banner Management")}
                      </h2>
                    </div>
                    <p className="text-gray-500 mt-2 ml-12">
                      {t("Create and manage promotional banners")}
                    </p>
                  </div>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                    <div className="inline-flex rounded-2xl border border-gray-200 p-1 bg-gray-50">
                      <button
                        type="button"
                        onClick={() => handleBannerMediaTabChange("image")}
                        className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition-all duration-300 ${
                          bannerMediaTab === "image"
                            ? "bg-white text-gray-900 shadow-sm"
                            : "text-gray-600 hover:text-gray-900"
                        }`}
                      >
                        <ImageIcon size={18} />
                        {t("Banner Images")}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleBannerMediaTabChange("video")}
                        className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition-all duration-300 ${
                          bannerMediaTab === "video"
                            ? "bg-white text-gray-900 shadow-sm"
                            : "text-gray-600 hover:text-gray-900"
                        }`}
                      >
                        <Video size={18} />
                        {t("Videos")}
                      </button>
                    </div>

                    <button
                      onClick={() => handleCreateBanner(bannerMediaTab)}
                      className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-gray-600 to-black hover:from-black hover:to-gray-700 text-white rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl font-semibold"
                    >
                      <Plus size={20} />
                      {t(
                        bannerMediaTab === "video" ? "Add Video" : "Add Banner",
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Banner List */}
              {!showBannerForm && (
                <div className="space-y-4">
                  {visibleBanners.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                        {bannerMediaTab === "video" ? (
                          <Video className="w-10 h-10 text-gray-400" />
                        ) : (
                          <Layout className="w-10 h-10 text-gray-400" />
                        )}
                      </div>
                      <p className="text-gray-500 font-medium mb-4">
                        {t(
                          bannerMediaTab === "video"
                            ? "No video banners created yet"
                            : "No banners created yet",
                        )}
                      </p>
                      <button
                        onClick={() => handleCreateBanner(bannerMediaTab)}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-gray-700 to-black hover:from-black hover:to-gray-700 text-white rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl font-semibold"
                      >
                        <Plus size={20} />
                        {t(
                          bannerMediaTab === "video"
                            ? "Create Video"
                            : "Create First Banner",
                        )}
                      </button>
                    </div>
                  ) : (
                    visibleBanners.map((banner, index) => (
                      <div
                        key={banner.id || index}
                        className="bg-white rounded-2xl border-2 border-gray-100 p-6 hover:shadow-lg transition-all duration-300"
                      >
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                          {/* Banner Preview */}
                          <div className="lg:col-span-4">
                            <div className="aspect-[21/9] w-full bg-gray-100 rounded-xl overflow-hidden">
                              {isVideoBanner(banner) ? (
                                <div className="w-full h-full flex flex-col items-center justify-center gap-3 bg-gray-900 text-white p-4">
                                  <Video className="w-12 h-12 text-gray-300" />
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setPreviewVideo({
                                        src: getBannerVideoSrc(banner),
                                        title:
                                          banner.title?.en ||
                                          banner.title?.ar ||
                                          t("Banner Video"),
                                      })
                                    }
                                    className="inline-flex items-center gap-2 rounded-xl bg-white px-4 py-2 text-sm font-semibold text-gray-900 transition-all duration-300 hover:bg-gray-100"
                                  >
                                    <Eye size={18} />
                                    {t("Preview Video")}
                                  </button>
                                </div>
                              ) : banner.image ? (
                                <img
                                  src={getImageSrc(banner?.image)}
                                  alt={banner.title?.en || "Banner"}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <ImageIcon className="w-12 h-12 text-gray-400" />
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Banner Info */}
                          <div className="lg:col-span-6">
                            <h3 className="text-xl font-bold text-gray-800 mb-2">
                              {(i18n.language === "ar"
                                ? banner.title?.ar
                                : banner.title?.en) || t("Untitled Banner")}
                            </h3>
                            {/* {banner.description?.en && (
                              <p className="text-gray-600 mb-3 line-clamp-2">
                                {banner.description.en}
                              </p>
                            )} */}
                            <div className="flex flex-wrap gap-2 mb-3">
                              <span
                                className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                  banner.isActive
                                    ? "bg-green-100 text-green-700"
                                    : "bg-gray-100 text-gray-600"
                                }`}
                              >
                                {banner.isActive ? t("Active") : t("Inactive")}
                              </span>
                              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-200 text-gray-700">
                                {banner.position === "home_main"
                                  ? t("Main")
                                  : t("Secondary")}
                              </span>
                              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-orange-200 text-gray-700">
                                {t("Order")}: {banner.sortOrder}
                              </span>
                              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-700">
                                {t(isVideoBanner(banner) ? "Video" : "Image")}
                              </span>
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="lg:col-span-2 flex lg:flex-col gap-2">
                            {isVideoBanner(banner) && (
                              <button
                                type="button"
                                onClick={() =>
                                  setPreviewVideo({
                                    src: getBannerVideoSrc(banner),
                                    title:
                                      banner.title?.en ||
                                      banner.title?.ar ||
                                      t("Banner Video"),
                                  })
                                }
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-black hover:bg-gray-800 text-white rounded-lg transition-all duration-300"
                                title={t("Preview Video")}
                              >
                                <Eye size={20} />
                              </button>
                            )}
                            <button
                              onClick={() => handleToggleBanner(banner.id!)}
                              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-all duration-300"
                              title={
                                banner.isActive
                                  ? t("Deactivate")
                                  : t("Activate")
                              }
                            >
                              {banner.isActive ? (
                                <ToggleRight
                                  size={20}
                                  className="text-green-600"
                                />
                              ) : (
                                <ToggleLeft size={20} />
                              )}
                            </button>
                            <button
                              onClick={() => handleEditBanner(banner)}
                              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-200 hover:bg-blue-500 text-blue-700 rounded-lg transition-all duration-300"
                              title={t("Edit")}
                            >
                              <Edit2 size={20} />
                            </button>
                            <button
                              onClick={() => openDeleteConfirm(banner.id!)}
                              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-all duration-300"
                              title={t("Delete")}
                            >
                              <Trash2 size={20} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Banner Form */}
              {showBannerForm && selectedBanner && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-2xl font-bold text-gray-800">
                      {selectedBanner.id
                        ? t(
                            bannerMediaTab === "video"
                              ? "Edit Video"
                              : "Edit Banner",
                          )
                        : t(
                            bannerMediaTab === "video"
                              ? "Create Video"
                              : "Create Banner",
                          )}
                    </h3>
                    <button
                      onClick={resetBannerForm}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-all duration-300"
                    >
                      <X size={24} />
                    </button>
                  </div>

                  {bannerMediaTab === "image" ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-3">
                          {t("Desktop Image")}{" "}
                          <span className="text-red-500">*</span>
                        </label>

                        <div className="mb-3">
                          <input
                            type="url"
                            value={localImagePreview}
                            onChange={(e) => {
                              const value = e.target.value;
                              setLocalImagePreview(value);
                              setImageFile(null);
                              setSelectedBanner({
                                ...selectedBanner,
                                image: value,
                                mediaType: "image",
                              });
                            }}
                            className="w-full px-4 py-2 rounded-lg border-2 border-gray-200 focus:border-[#0d223f] focus:outline-none text-sm"
                            placeholder={t("Or paste image URL here")}
                          />
                        </div>

                        <div className="aspect-[21/9] w-full bg-gray-100 rounded-xl overflow-hidden mb-4 border-2 border-dashed border-gray-300 relative group">
                          {localImagePreview ? (
                            <>
                              <img
                                src={getImageSrc(localImagePreview)}
                                alt="Desktop Banner"
                                className="w-full h-full object-cover"
                              />
                              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center">
                                <button
                                  onClick={() => {
                                    setLocalImagePreview("");
                                    setImageFile(null);
                                    setSelectedBanner({
                                      ...selectedBanner,
                                      image: "",
                                    });
                                  }}
                                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-all duration-300"
                                >
                                  <Trash2 size={18} />
                                </button>
                              </div>
                            </>
                          ) : (
                            <div className="w-full h-full flex flex-col items-center justify-center gap-3">
                              <ImageIcon className="w-12 h-12 text-gray-400" />
                              <p className="text-gray-400 font-medium">
                                {t("No image uploaded")}
                              </p>
                            </div>
                          )}
                        </div>
                        <label className="flex items-center justify-center gap-3 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl transition-all duration-300 cursor-pointer font-semibold shadow-lg hover:shadow-xl">
                          <Upload size={20} />
                          {uploadingBannerImage
                            ? t("Uploading...")
                            : t("Upload Image")}
                          <input
                            type="file"
                            accept="image/*"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                try {
                                  if (file.size > 2 * 1024 * 1024) {
                                    throw new Error(
                                      "Image size must be under 2MB",
                                    );
                                  }
                                  const supportingUploadSaperately = false;
                                  if (supportingUploadSaperately) {
                                    handleBannerImageUpload(file, false);
                                  } else {
                                    await validateImageDimensions(
                                      file,
                                      1600,
                                      400,
                                    );
                                    const previewUrl = URL.createObjectURL(file);
                                    setLocalImagePreview(previewUrl);
                                    const base64 = await fileToBase64(file);
                                    setImageFile(base64);
                                    setSelectedBanner((prev) =>
                                      prev
                                        ? {
                                            ...prev,
                                            image: base64,
                                            mediaType: "image",
                                          }
                                        : null,
                                    );
                                  }
                                } catch (err: any) {
                                  alert(err.message);
                                }
                              }
                            }}
                            className="hidden"
                            disabled={uploadingBannerImage}
                          />
                        </label>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-3">
                          {t("Video")} <span className="text-red-500">*</span>
                        </label>

                        <div className="mb-3">
                          <input
                            type="url"
                            value={localVideoPreview}
                            onChange={(e) => {
                              const value = e.target.value;
                              setLocalVideoPreview(value);
                              setVideoFile(null);
                              setSelectedBanner({
                                ...selectedBanner,
                                video: value,
                                mobileVideo: value,
                                videoUrl: value,
                                mediaType: "video",
                              });
                            }}
                            className="w-full px-4 py-2 rounded-lg border-2 border-gray-200 focus:border-[#0d223f] focus:outline-none text-sm"
                            placeholder={t("Or paste video URL here")}
                          />
                        </div>

                        <div className="aspect-[21/9] w-full bg-gray-100 rounded-xl overflow-hidden mb-4 border-2 border-dashed border-gray-300 relative">
                          {localVideoPreview ? (
                            <div className="w-full h-full flex flex-col items-center justify-center gap-4 bg-gray-900 text-white p-6">
                              <Video className="w-12 h-12 text-gray-300" />
                              <div className="flex flex-wrap items-center justify-center gap-3">
                                <button
                                  type="button"
                                  onClick={() =>
                                    setPreviewVideo({
                                      src: localVideoPreview,
                                      title:
                                        selectedBanner.title?.en ||
                                        selectedBanner.title?.ar ||
                                        t("Banner Video"),
                                    })
                                  }
                                  className="inline-flex items-center gap-2 rounded-xl bg-white px-4 py-2 text-sm font-semibold text-gray-900 transition-all duration-300 hover:bg-gray-100"
                                >
                                  <Eye size={18} />
                                  {t("Preview Video")}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setLocalVideoPreview("");
                                    setVideoFile(null);
                                    setSelectedBanner({
                                      ...selectedBanner,
                                      video: "",
                                      mobileVideo: "",
                                      videoUrl: "",
                                    });
                                  }}
                                  className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white transition-all duration-300 hover:bg-red-700"
                                >
                                  <Trash2 size={18} />
                                  {t("Remove")}
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="w-full h-full flex flex-col items-center justify-center gap-3">
                              <Video className="w-12 h-12 text-gray-400" />
                              <p className="text-gray-400 font-medium">
                                {t("No video uploaded")}
                              </p>
                            </div>
                          )}
                        </div>
                        <label className="flex items-center justify-center gap-3 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl transition-all duration-300 cursor-pointer font-semibold shadow-lg hover:shadow-xl">
                          <Upload size={20} />
                          {t("Upload Video")}
                          <input
                            type="file"
                            accept="video/*"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                try {
                                  const previewUrl = URL.createObjectURL(file);
                                  setLocalVideoPreview(previewUrl);
                                  setVideoFile(file);
                                  setSelectedBanner((prev) =>
                                    prev
                                      ? {
                                          ...prev,
                                          video: "",
                                          mobileVideo: "",
                                          videoUrl: "",
                                          mediaType: "video",
                                        }
                                      : null,
                                  );
                                } catch (uploadError: any) {
                                  alert(
                                    uploadError?.message ||
                                      "Failed to process the video",
                                  );
                                }
                              }
                            }}
                            className="hidden"
                          />
                        </label>
                        <p className="mt-3 text-sm text-gray-500">
                          {t(
                            "You can upload and manage multiple videos. Use active and inactive to control which videos customers see.",
                          )}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Banner Details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Title English */}
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        {t("Title")} (EN){" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={selectedBanner.title.en}
                        onChange={(e) =>
                          setSelectedBanner({
                            ...selectedBanner,
                            title: {
                              ...selectedBanner.title,
                              en: e.target.value,
                            },
                          })
                        }
                        className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#0d223f] focus:outline-none"
                        placeholder={t("Enter banner title")}
                      />
                    </div>

                    {/* Title Arabic */}
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        {t("Title")} (AR)
                      </label>
                      <input
                        type="text"
                        value={selectedBanner.title.ar}
                        onChange={(e) =>
                          setSelectedBanner({
                            ...selectedBanner,
                            title: {
                              ...selectedBanner.title,
                              ar: e.target.value,
                            },
                          })
                        }
                        className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#0d223f] focus:outline-none"
                        placeholder={t("Enter banner title in Arabic")}
                      />
                    </div>

                    {/* Position */}
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        {t("Position")}
                      </label>
                      <select
                        value={selectedBanner.position}
                        onChange={(e) =>
                          setSelectedBanner({
                            ...selectedBanner,
                            position: e.target.value as
                              | "home_main"
                              | "home_secondary",
                          })
                        }
                        className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#0d223f] focus:outline-none"
                      >
                        <option value="home_main">{t("Home - Main")}</option>
                        <option value="home_secondary">
                          {t("Home - Secondary")}
                        </option>
                      </select>
                    </div>

                    {/* Sort Order */}
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        {t("Sort Order")}
                      </label>
                      <input
                        type="number"
                        value={selectedBanner.sortOrder}
                        onChange={(e) =>
                          setSelectedBanner({
                            ...selectedBanner,
                            sortOrder: parseInt(e.target.value),
                          })
                        }
                        className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#0d223f] focus:outline-none"
                        min="0"
                      />
                    </div>

                    {/* Start Date */}
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        {t("Start Date")}
                      </label>
                      <input
                        type="datetime-local"
                        value={
                          selectedBanner.startDate
                            ? new Date(selectedBanner.startDate)
                                .toISOString()
                                .slice(0, 16)
                            : ""
                        }
                        onChange={(e) =>
                          setSelectedBanner({
                            ...selectedBanner,
                            startDate: e.target.value,
                          })
                        }
                        className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#0d223f] focus:outline-none"
                      />
                    </div>

                    {/* End Date */}
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        {t("End Date")}{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="datetime-local"
                        required
                        value={
                          selectedBanner.endDate
                            ? new Date(selectedBanner.endDate)
                                .toISOString()
                                .slice(0, 16)
                            : ""
                        }
                        onChange={(e) =>
                          setSelectedBanner({
                            ...selectedBanner,
                            endDate: e.target.value,
                          })
                        }
                        className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#0d223f] focus:outline-none"
                      />
                    </div>

                    {/* Active Status */}
                    <div className="md:col-span-2">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedBanner.isActive}
                          onChange={(e) =>
                            setSelectedBanner({
                              ...selectedBanner,
                              isActive: e.target.checked,
                            })
                          }
                          className="w-5 h-5 rounded border-2 border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm font-bold text-gray-700">
                          {t("Active")}
                        </span>
                      </label>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end gap-4 pt-6 border-t border-gray-600">
                    <button
                      onClick={resetBannerForm}
                      className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-all duration-300 font-semibold"
                    >
                      {t("Cancel")}
                    </button>
                    <button
                      onClick={handleSaveBanner}
                      disabled={
                        saving ||
                        !selectedBanner.title.en ||
                        !selectedBanner.endDate
                      }
                      className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-gray-700 to-black hover:from-gray-500 hover:to-black text-white rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {saving ? (
                        <>
                          <RefreshCw size={20} className="animate-spin" />
                          {t("Saving...")}
                        </>
                      ) : (
                        <>
                          <Save size={20} />
                          {t(
                            bannerMediaTab === "video"
                              ? "Save Video"
                              : "Save Banner",
                          )}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === "advanced" && (
            <div className="space-y-6">
              <div className="pb-6 border-b border-gray-100">
                <div className="flex items-center gap-3 mb-2">
                  <div className="bg-gradient-to-br from-gray-500 to-gray-600 rounded-lg p-2">
                    <Settings size={22} className="text-white" />
                  </div>
                  <h2 className="text-3xl font-bold text-gray-800">
                    {t("Advanced Settings")}
                  </h2>
                </div>
                <p className="text-gray-500 mt-2 ml-12">
                  {t("Configure advanced company preferences")}
                </p>
              </div>

              <div className="space-y-4">
                <div className="bg-white rounded-2xl border-2 border-gray-100 p-6">
                  <div className="mb-6">
                    <h3 className="text-xl font-bold text-gray-800">
                      {t("Payments")}
                    </h3>
                  </div>

                  {settingsLoading ? (
                    <div className="flex items-center gap-3 text-gray-500">
                      <RefreshCw size={18} className="animate-spin" />
                      <span>{t("Loading...")}</span>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 rounded-xl border border-gray-200 p-5 bg-gray-50/60">
                        <div>
                          <h4 className="text-lg font-semibold text-gray-800">
                            {t("Pay and deliver")}
                          </h4>
                          <p className="text-sm text-gray-500 mt-1">
                            {t("Disable/Enable pay and deliver")}
                          </p>
                        </div>

                        <button
                          type="button"
                          onClick={handleTogglePayAndDeliver}
                          disabled={updatingPayAndDeliver}
                          className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl font-semibold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed ${
                            payAndDeliver
                              ? "bg-green-100 text-green-700"
                              : "bg-gray-200 text-gray-600"
                          }`}
                        >
                          {updatingPayAndDeliver ? (
                            <RefreshCw size={18} className="animate-spin" />
                          ) : payAndDeliver ? (
                            <ToggleRight size={28} className="text-green-600" />
                          ) : (
                            <ToggleLeft size={28} className="text-gray-500" />
                          )}
                          <span>
                            {payAndDeliver ? t("Enabled") : t("Disabled")}
                          </span>
                        </button>
                      </div>

                      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 rounded-xl border border-gray-200 p-5 bg-gray-50/60">
                        <div className="flex-1">
                          <h4 className="text-lg font-semibold text-gray-800">
                            {t("Payment Provider")}
                          </h4>
                          <p className="text-sm text-gray-500 mt-1">
                            {t("Active online payment provider")}
                          </p>
                          <select
                            value={paymentProvider}
                            onChange={(e) => setPaymentProvider(e.target.value)}
                            disabled={updatingPaymentProvider}
                            className="mt-4 w-full max-w-md px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-gray-400 focus:outline-none transition-colors bg-white disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                          >
                            {paymentProviderOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <button
                          type="button"
                          onClick={handleSavePaymentProvider}
                          disabled={
                            updatingPaymentProvider ||
                            paymentProvider === savedPaymentProvider
                          }
                          className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gray-900 text-white font-semibold hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {updatingPaymentProvider ? (
                            <RefreshCw size={18} className="animate-spin" />
                          ) : (
                            <Save size={18} />
                          )}
                          <span>{t("Save")}</span>
                        </button>
                      </div>

                      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 rounded-xl border border-gray-200 p-5 bg-gray-50/60">
                        <div className="flex-1">
                          <h4 className="text-lg font-semibold text-gray-800">
                            {t("OTP Channel")}
                          </h4>
                          <p className="text-sm text-gray-500 mt-1">
                            {t("Channel used to send customer OTP codes")}
                          </p>
                          <select
                            value={otpChannel}
                            onChange={(e) => setOtpChannel(e.target.value)}
                            disabled={updatingOtpChannel}
                            className="mt-4 w-full max-w-md px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-gray-400 focus:outline-none transition-colors bg-white disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                          >
                            {OTP_CHANNEL_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {t(option.label)}
                              </option>
                            ))}
                          </select>
                        </div>

                        <button
                          type="button"
                          onClick={handleSaveOtpChannel}
                          disabled={
                            updatingOtpChannel || otpChannel === savedOtpChannel
                          }
                          className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gray-900 text-white font-semibold hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {updatingOtpChannel ? (
                            <RefreshCw size={18} className="animate-spin" />
                          ) : (
                            <Save size={18} />
                          )}
                          <span>{t("Save")}</span>
                        </button>
                      </div>

                    </div>
                  )}
                </div>

                <div className="bg-white rounded-2xl border-2 border-gray-100 p-6">
                  <div className="mb-6">
                    <h3 className="text-xl font-bold text-gray-800">
                      {t("Banner Display Mode")}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      {t(
                        "Choose whether the public homepage banner shows image banners or video banners.",
                      )}
                    </p>
                  </div>

                  {settingsLoading ? (
                    <div className="flex items-center gap-3 text-gray-500">
                      <RefreshCw size={18} className="animate-spin" />
                      <span>{t("Loading...")}</span>
                    </div>
                  ) : (
                    <div className="rounded-xl border border-gray-200 p-5 bg-gray-50/60">
                      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
                        <div className="flex-1">
                          <h4 className="text-lg font-semibold text-gray-800">
                            {t("Public Banner Media")}
                          </h4>
                          <p className="text-sm text-gray-500 mt-1">
                            {t(
                              "This controls whether customers see image banners or video banners on the homepage.",
                            )}
                          </p>
                          <select
                            value={bannerDisplayMode}
                            onChange={(e) =>
                              setBannerDisplayMode(
                                e.target.value as BannerDisplayMode,
                              )
                            }
                            disabled={updatingBannerDisplayMode}
                            className="mt-4 w-full max-w-md px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-gray-400 focus:outline-none transition-colors bg-white disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                          >
                            <option value="image">{t("Image")}</option>
                            <option value="video">{t("Video")}</option>
                          </select>
                        </div>

                        <button
                          type="button"
                          onClick={handleSaveBannerDisplayMode}
                          disabled={
                            updatingBannerDisplayMode ||
                            bannerDisplayMode === savedBannerDisplayMode
                          }
                          className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gray-900 text-white font-semibold hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {updatingBannerDisplayMode ? (
                            <RefreshCw size={18} className="animate-spin" />
                          ) : (
                            <Save size={18} />
                          )}
                          <span>{t("Save")}</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="bg-white rounded-2xl border-2 border-gray-100 p-6">
                  <div className="mb-6">
                    <h3 className="text-xl font-bold text-gray-800">
                      {t("Report Recipients")}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      {t(
                        "Configure the emails and numbers that should receive all scheduled reports.",
                      )}
                    </p>
                  </div>

                  {settingsLoading ? (
                    <div className="flex items-center gap-3 text-gray-500">
                      <RefreshCw size={18} className="animate-spin" />
                      <span>{t("Loading...")}</span>
                    </div>
                  ) : (
                    <div className="rounded-xl border border-gray-200 p-5 bg-gray-50/60">
                      <div className="flex items-center justify-between gap-4 mb-4">
                        <div>
                          <h4 className="text-lg font-semibold text-gray-800">
                            {t("All Scheduled Reports")}
                          </h4>
                          <p className="text-sm text-gray-500 mt-1">
                            {t(
                              "These emails and numbers will receive daily, weekly, monthly, quarterly, half yearly, and yearly reports.",
                            )}
                          </p>
                        </div>

                        {editingReportRecipients ? (
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={handleCancelReportRecipientEdit}
                              disabled={savingReportRecipients}
                              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-200 text-gray-700 font-semibold hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <X size={16} />
                              <span>{t("Cancel")}</span>
                            </button>
                            <button
                              type="button"
                              onClick={handleSaveReportRecipients}
                              disabled={savingReportRecipients}
                              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-900 text-white font-semibold hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {savingReportRecipients ? (
                                <RefreshCw size={16} className="animate-spin" />
                              ) : (
                                <Save size={16} />
                              )}
                              <span>{t("Save")}</span>
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setEditingReportRecipients(true)}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-900 text-white font-semibold hover:bg-gray-800 transition-colors"
                          >
                            <Edit2 size={16} />
                            <span>{t("Edit")}</span>
                          </button>
                        )}
                      </div>

                      <div className="mb-4 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
                        <p className="font-medium">
                          {t(
                            "Note: You can add multiple emails and numbers separated by commas.",
                          )}
                        </p>
                        <p className="mt-1 text-blue-800">
                          {t(
                            "Example emails: finance@example.com, owner@example.com",
                          )}
                        </p>
                        <p className="mt-1 text-blue-800">
                          {t("Example numbers: +971500000000, +966500000000")}
                        </p>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t("Emails")}
                          </label>
                          <textarea
                            value={reportRecipientInputs.emails}
                            onChange={(e) =>
                              handleReportRecipientInputChange(
                                "emails",
                                e.target.value,
                              )
                            }
                            disabled={!editingReportRecipients}
                            rows={5}
                            placeholder={t(
                              "finance@example.com\nowner@example.com",
                            )}
                            className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-gray-400 focus:outline-none transition-colors resize-none bg-white disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t("Numbers")}
                          </label>
                          <textarea
                            value={reportRecipientInputs.numbers}
                            onChange={(e) =>
                              handleReportRecipientInputChange(
                                "numbers",
                                e.target.value,
                              )
                            }
                            disabled={!editingReportRecipients}
                            rows={5}
                            placeholder={t("+971500000000\n+966500000000")}
                            className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-gray-400 focus:outline-none transition-colors resize-none bg-white disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {previewVideo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="w-full max-w-4xl rounded-3xl bg-white shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <h3 className="text-lg font-bold text-gray-900">
                {previewVideo.title}
              </h3>
              <button
                type="button"
                onClick={() => setPreviewVideo(null)}
                className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900"
              >
                <X size={20} />
              </button>
            </div>
            <div className="bg-black p-4">
              <video
                src={previewVideo.src}
                controls
                autoPlay
                className="max-h-[70vh] w-full rounded-2xl bg-black"
              />
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmConfig.isOpen}
        title={confirmConfig.title}
        message={confirmConfig.message}
        confirmText={confirmConfig.confirmText}
        cancelText={confirmConfig.cancelText}
        icon={Trash2}
        iconColor="text-red-600"
        iconBg="bg-red-100"
        onConfirm={confirmConfig.onConfirm}
        onCancel={closeConfirmModal}
      />
    </div>
  );
};

export default CompanySettingsPage;
